// src/components/healthcare/schemaBuilder/SchemaNodeComponent.tsx

import React, { useState } from "react";
import { Card, Input, Switch, Button, Space, Tooltip, Tag, Select, Typography, Dropdown } from "antd";
import type { MenuProps } from "antd";
import {
	DragOutlined,
	EditOutlined,
	DeleteOutlined,
	CopyOutlined,
	PlusOutlined,
	DownOutlined,
	UpOutlined,
	FolderOutlined,
	FileTextOutlined,
	FontSizeOutlined,
	NumberOutlined,
	CheckCircleOutlined,
	UnorderedListOutlined,
	OrderedListOutlined,
	GroupOutlined,
} from "@ant-design/icons";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { SchemaNode, SchemaFieldType } from "../../types/appointments/SchemaBuilderTypes";

const { TextArea } = Input;
const { Text } = Typography;

// Colour config — one accent per type for left border, icon, and tag
const TYPE_CONFIG: Record<SchemaFieldType, { color: string; tagBg: string; tagColor: string }> = {
	section: { color: "#6366f1", tagBg: "#eef2ff", tagColor: "#4338ca" },
	topic:   { color: "#3b82f6", tagBg: "#eff6ff", tagColor: "#1d4ed8" },
	object:  { color: "#8b5cf6", tagBg: "#f5f3ff", tagColor: "#6d28d9" },
	text:    { color: "#94a3b8", tagBg: "#f8fafc", tagColor: "#64748b" },
	number:  { color: "#f59e0b", tagBg: "#fffbeb", tagColor: "#d97706" },
	boolean: { color: "#10b981", tagBg: "#ecfdf5", tagColor: "#059669" },
	enum:    { color: "#f43f5e", tagBg: "#fff1f2", tagColor: "#e11d48" },
	array:   { color: "#f97316", tagBg: "#fff7ed", tagColor: "#ea580c" },
};

const TYPE_ICONS: Record<SchemaFieldType, React.ReactNode> = {
	section: <FolderOutlined />,
	topic: <FileTextOutlined />,
	object: <GroupOutlined />,
	text: <FontSizeOutlined />,
	number: <NumberOutlined />,
	boolean: <CheckCircleOutlined />,
	enum: <UnorderedListOutlined />,
	array: <OrderedListOutlined />,
};

export const SchemaNodeComponent: React.FC<{
	node: SchemaNode;
	onUpdate: (nodeId: string, updates: Partial<SchemaNode>) => void;
	onDelete: (nodeId: string) => void;
	onDuplicate: (nodeId: string) => void;
	onAddChild: (type: SchemaFieldType, parentId: string) => void;
	level: number;
	isDragOver?: boolean;
}> = ({ node, onUpdate, onDelete, onDuplicate, onAddChild, level, isDragOver }) => {
	const [editing, setEditing] = useState(false);
	const [expanded, setExpanded] = useState(level < 2);

	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: node.id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.4 : 1,
	};

	const canHaveChildren = ["section", "topic", "object", "array"].includes(node.type);
	const hasChildren = node.children && node.children.length > 0;

	const quickAddMenu = [
		{ type: "text" as SchemaFieldType, label: "Text", icon: <FontSizeOutlined /> },
		{ type: "number" as SchemaFieldType, label: "Number", icon: <NumberOutlined /> },
		{ type: "boolean" as SchemaFieldType, label: "Yes/No", icon: <CheckCircleOutlined /> },
		{ type: "enum" as SchemaFieldType, label: "Choice", icon: <UnorderedListOutlined /> },
		{ type: "array" as SchemaFieldType, label: "List", icon: <OrderedListOutlined /> },
		{ type: "object" as SchemaFieldType, label: "Group", icon: <GroupOutlined /> },
	];

	const addChildMenuItems: MenuProps["items"] = quickAddMenu.map(item => ({
		key: item.type,
		icon: item.icon,
		label: item.label,
		onClick: () => onAddChild(item.type, node.id),
	}));

	return (
		<div ref={setNodeRef} style={{ ...style, marginLeft: level * 16 }}>
			{/* Drop indicator — shown when a palette item is dragged over this node */}
			{isDragOver && (
				<div className="h-0.5 bg-blue-400 rounded mb-1 mx-1" />
			)}
			<Card
				size="small"
				className={`mb-1.5 ${isDragging ? "shadow-lg" : ""} ${isDragOver ? "ring-1 ring-blue-300" : ""}`}
				styles={{ body: { padding: "8px 12px" } }}
				style={{ borderLeft: `3px solid ${TYPE_CONFIG[node.type].color}` }}
			>
				{/* Header */}
				<div className="flex items-center gap-2">
					<div {...attributes} {...listeners} className="cursor-move text-gray-300 hover:text-gray-500 flex-shrink-0">
						<DragOutlined />
					</div>

					<span className="flex-shrink-0 text-sm" style={{ color: TYPE_CONFIG[node.type].color }}>
						{TYPE_ICONS[node.type]}
					</span>

					{editing ? (
						<div className="flex items-center gap-1.5 flex-1 min-w-0">
							<Input size="small" placeholder="Label" value={node.label} onChange={(e) => onUpdate(node.id, { label: e.target.value })} style={{ width: 140 }} />
							<Input size="small" placeholder="key" value={node.key} onChange={(e) => onUpdate(node.id, { key: e.target.value })} className="font-mono" style={{ width: 110 }} />
						</div>
					) : (
						<div className="flex-1 min-w-0">
							<div className="flex items-center gap-1.5">
								<Text className="text-xs font-medium truncate">{node.label}</Text>
							<Tag
								className="text-[9px] px-1 py-0 leading-4 border-0"
								style={{ background: TYPE_CONFIG[node.type].tagBg, color: TYPE_CONFIG[node.type].tagColor }}
							>
								{node.type}
							</Tag>
								{node.required && (
									<Tag color="red" className="text-[9px] px-1 py-0 leading-4">*</Tag>
								)}
							</div>
							<Text className="text-[10px] text-gray-400 font-mono block truncate">{node.key}</Text>
							{node.description && (
								<Text className="text-[10px] text-gray-400 italic block truncate mt-0.5">{node.description}</Text>
							)}
						</div>
					)}

					{canHaveChildren && hasChildren && (
						<Button type="text" size="small" icon={expanded ? <UpOutlined /> : <DownOutlined />} onClick={() => setExpanded(!expanded)} className="flex-shrink-0 text-gray-400" />
					)}

					<Space size={0} className="flex-shrink-0">
						<Tooltip title={editing ? "Done" : "Edit"}>
							<Button type="text" size="small" icon={<EditOutlined />} onClick={() => setEditing(!editing)} className={`px-1.5 ${editing ? "text-blue-500" : "text-gray-400 hover:text-gray-600"}`} />
						</Tooltip>

						{canHaveChildren && (
							<Dropdown menu={{ items: addChildMenuItems }} trigger={["click"]} placement="bottomRight">
								<Button type="text" size="small" icon={<PlusOutlined />} className="px-1.5 text-gray-400 hover:text-gray-600" title="Add field" />
							</Dropdown>
						)}

						<Tooltip title="Duplicate">
							<Button type="text" size="small" icon={<CopyOutlined />} onClick={() => onDuplicate(node.id)} className="px-1.5 text-gray-400 hover:text-gray-600" />
						</Tooltip>

						<Tooltip title="Delete">
							<Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => onDelete(node.id)} className="px-1.5" />
						</Tooltip>
					</Space>
				</div>

				{/* Configuration Panel */}
				{editing && (
					<div className="mt-2 pt-2 border-t border-gray-100 space-y-2">
						<div>
							<Text className="text-[10px] text-gray-500 block mb-1">
								Description <span className="text-gray-400">(included in AI prompt)</span>
							</Text>
							<TextArea
								size="small"
								rows={2}
								placeholder="Describe what data this field should contain…"
								value={node.description}
								onChange={(e) => onUpdate(node.id, { description: e.target.value })}
							/>
						</div>

						{node.type === "text" && (
							<div>
								<Text className="text-[10px] text-gray-500 block mb-1">Placeholder</Text>
								<Input size="small" placeholder="e.g., Enter value…" value={node.placeholder} onChange={(e) => onUpdate(node.id, { placeholder: e.target.value })} />
							</div>
						)}

						{node.type === "enum" && (
							<div>
								<Text className="text-[10px] text-gray-500 block mb-1">Options (comma-separated)</Text>
								<Input
									size="small"
									placeholder="Option 1, Option 2"
									value={node.enumConfig?.options.join(", ")}
									onChange={(e) =>
										onUpdate(node.id, {
											enumConfig: {
												options: e.target.value
													.split(",")
													.map((s) => s.trim())
													.filter(Boolean),
											},
										})
									}
								/>
							</div>
						)}

						{node.type === "array" && (
							<div>
								<Text className="text-[10px] text-gray-500 block mb-1">Item Type</Text>
								<Select
									size="small"
									value={node.arrayConfig?.itemType || "text"}
									onChange={(value) =>
										onUpdate(node.id, {
											arrayConfig: { ...node.arrayConfig, itemType: value as SchemaFieldType },
										})
									}
									className="w-full"
									options={[
										{ label: "Text", value: "text" },
										{ label: "Number", value: "number" },
										{ label: "Object", value: "object" },
									]}
								/>
							</div>
						)}

						<div className="flex items-center justify-between">
							<Text className="text-[10px] text-gray-500">Required</Text>
							<Switch size="small" checked={node.required} onChange={(checked) => onUpdate(node.id, { required: checked })} />
						</div>
					</div>
				)}

				{/* Children */}
				{canHaveChildren && hasChildren && expanded && (
					<div className="mt-2 pl-2 border-l-2" style={{ borderColor: `${TYPE_CONFIG[node.type].color}30` }}>
						{node.children!.map((child) => (
							<SchemaNodeComponent key={child.id} node={child} onUpdate={onUpdate} onDelete={onDelete} onDuplicate={onDuplicate} onAddChild={onAddChild} level={level + 1} />
						))}
					</div>
				)}

				{/* Empty state for containers */}
				{canHaveChildren && !hasChildren && expanded && (
					<div
						className="mt-2 p-3 rounded text-center"
						style={{ border: `1px dashed ${TYPE_CONFIG[node.type].color}50`, background: `${TYPE_CONFIG[node.type].tagBg}` }}
					>
						<Text className="text-[10px] text-gray-400 block mb-2">No fields yet</Text>
						<Space size={4} wrap>
							{quickAddMenu.map((item) => (
								<Button key={item.type} size="small" icon={item.icon} onClick={() => onAddChild(item.type, node.id)} className="text-[10px] h-6">
									{item.label}
								</Button>
							))}
						</Space>
					</div>
				)}
			</Card>
		</div>
	);
};

export default SchemaNodeComponent;
